import { useSearchParams } from 'react-router-dom';

import { DateTime } from 'luxon';

export type TRange = [DateTime | null, DateTime | null];

export type UseDateRangeQueryPropsType = {
	startParam?: string;
	endParam?: string;
	defaultFormat?: 'month' | 'week' | 'day';
};

export default function useDateRangeQuery({
	startParam = 'startDate',
	endParam = 'endDate',
	defaultFormat = 'month',
}: UseDateRangeQueryPropsType = {}) {
	const [searchParams, setSearchParams] = useSearchParams();

	const parseDate = (param: string): DateTime | null => {
		const date = searchParams.get(param);
		return date ? DateTime.fromISO(date) : null;
	};

	// eslint-disable-next-line no-confusing-arrow
	const formatDate = (date: DateTime | null): string | undefined =>
		date ? date.toISODate() : undefined;

	const startDateTime = parseDate(startParam) || DateTime.now().startOf(defaultFormat);
	const endDateTime = parseDate(endParam) || DateTime.now().endOf(defaultFormat);

	const setDateRange = (newRange: TRange) => {
		const [newStart, newEnd] = newRange;

		setSearchParams((prev) => {
			if (newStart) {
				prev.set(startParam, formatDate(newStart)!);
			} else {
				prev.delete(startParam);
			}

			if (newEnd) {
				prev.set(endParam, formatDate(newEnd)!);
			} else {
				prev.delete(endParam);
			}

			return prev;
		});
	};

	return {
		range: [startDateTime, endDateTime] as TRange,
		setRange: setDateRange,
		startDate: searchParams.get(startParam),
		endDate: searchParams.get(endParam),
		startDateTime,
		endDateTime,
		setStartDate: (newStart: DateTime | null) => setDateRange([newStart, endDateTime]),
		setEndDate: (newEnd: DateTime | null) => setDateRange([startDateTime, newEnd]),
	};
}
